// Every line Account shows, in 1 place. The board's own words (Account.dc.html) are copied exactly;
// the lines the board doesn't draw are marked "undrawn" and wait for Adam's batch approval. The
// functions' refusals (set-password, change-email, log-out-everywhere) come with their own line in
// the answer's message, so they aren't repeated here: docs/data-model.md 10.1 lists them.
import { SITE } from '../config/site.js'

// The 1 other site that logs in through the same Supabase project: Profit On Your Terms's own, while it runs. The
// Accelerator's, members.adamslee.co.uk, forwards every address to the hub since 22 Oct and logs nobody in, so its
// logins aren't shared any more (migration 82, 20261024090000)
const SHARED_SITE = 'course.adamslee.co.uk'

export const COPY = {
  title: 'Your account',
  navLabel: 'Account sections',

  // Profile
  profile: 'Profile',
  name: 'Name',
  location: 'Where you invest',
  email: 'Email',
  emailHint: 'This is the email you log in with. Changing it sends a code to the new address first.',
  save: 'Save changes',
  saved: 'Saved',
  // undrawn
  nameEmpty: 'Type your name.',
  saveFailed: "Your changes didn't save. Check your connection and try again.",
  // the Login screen's verb (Type), as change-email's own line
  emailInvalid: 'Type a valid email address.',
  // Accelerator members only (Adam, 2 Oct): the board's Change photo, About you and the directory
  // switch, for the Group page's member directory
  changePhoto: 'Change photo',
  about: "About you, for the Accelerator's member directory",
  directory: "Show me in the Property Accelerator's member directory",
  // a member of another mastermind (the product-types follow-ups, 13 Oct): a switch for each mastermind
  // they hold, each its own directory (hub.directory_listings); the Accelerator's switch reads the line
  // above word for word, and a member of the Accelerator alone reads the 2 lines above
  aboutFor: n => (n > 1 ? 'About you, for the member directories' : 'About you, for the member directory'),
  directoryOf: name => `Show me in ${name}'s member directory`,
  // undrawn: another mastermind's switch couldn't be read, so it can't be changed here just now
  listingsFailed: "A member directory switch didn't load. Reload the page to try again.",
  // undrawn: while the photo goes up, and once it has
  photoAdding: 'Adding the photo',
  photoAdded: 'Photo added.',
  // sign-upload's own lines (docs/data-model.md 10.6.3), checked here first
  photoType: 'A photo has to be a JPEG, PNG or WebP.',
  photoTooBig: 'That photo is bigger than 2 MB. Add a smaller one.',
  photoFailed: "The photo couldn't be added just now. Try again in a minute.",
  // undrawn: the database's limit on About you
  aboutLength: 'About you can be up to 400 characters.',
  // undrawn: the code step after a reload, when the change is already waiting
  codeWaiting: (email, until) => `We've sent an 8-digit code to ${email}. It works until ${until}.`,
  // the functions' own lines, for an answer that comes without one
  codeSent: email => `We've sent an 8-digit code to ${email}. It works for 15 minutes.`,
  emailDone: email => `Done. You log in with ${email} from now on.`,
  passwordSaved: 'Password saved. You can log in with it or with a code. Other devices will ask you to log in again.',
  codeLabel: 'Your code',
  codeBad: 'Type the 8-digit code from the email.',
  confirmEmail: 'Change email',
  resend: 'Send a new code',
  resent: 'New code sent',
  differentEmail: 'Use a different email',
  // C37 (Adam, 2 Oct): a login shared with POYT's site (since migration 82, the Accelerator's old site no
  // longer counts). Undrawn; change-email answers with its own line (409 shared_login), and Account shows
  // this one in its place.
  sharedEmail: `You log in here with the same login as ${SHARED_SITE}, so your email can't be changed here yet. Email ${SITE.helpEmail} and Adam will change it in both places.`,
  // under Log out on every device only (member review, 11 Oct), naming the button, since a plain Log out
  // sits beside it. my_login_shared says only that the login is shared; POYT's is the only site it can
  // be shared with now.
  sharedLogOut: `Log out on every device also logs you out of ${SHARED_SITE}.`,

  // Logging in
  loggingIn: 'Logging in',
  loginLede: "You log in with a code we email you. You can set a password as well if you'd rather type one.",
  // undrawn: in place of the board's lede once a password is set (Adam, 3 Oct)
  loginLedePassword: 'You log in with a code we email you, or with your password.',
  // undrawn (C37): a shared login can't set a password here, but may already have one from that
  // site, which works on the hub's password door too. 1 line in place of the board's lede.
  loginLedeShared: `You log in with a code we email you, or with the password you use on ${SHARED_SITE}, if you have one. To change that password, email ${SITE.helpEmail}.`,
  setPassword: 'Set a password',
  // undrawn: once people.password_set_at is set
  changePassword: 'Change your password',
  logOutEverywhere: 'Log out on every device',
  loggedOutEverywhere: 'Done. Every other device is logged out.',
  // undrawn: in place of log-out-everywhere's own line for a shared login, which still names both sites until that
  // function is deployed again (the review of 23 Oct), so the screen names only the site that still shares a login
  loggedOutEverywhereShared: `Done. Every other device is logged out of this site and of ${SHARED_SITE}, which uses the same login.`,
  // undrawn: the plain log out the hub has had since stage 1 (a departure: no board draws it)
  logOut: 'Log out',
  // undrawn: the password form
  newPassword: 'New password',
  passwordHint: 'At least 10 characters.',
  savePassword: 'Save password',
  cancel: 'Cancel',
  // undrawn: in place of passwordSaved when the password is saved but this device couldn't log in
  // again with it (Adam, 3 Oct)
  passwordSavedSignInAgain: 'Password saved. Log in again with it, or with a code, on this device and any other.',

  // My programmes
  programmes: 'My programmes',
  colProgramme: 'Programme',
  colWhat: 'What you have',
  colTerms: 'How long it lasts',
  readTerms: 'Read the full terms',
  // undrawn (the task's wording for a secured place)
  placeSecured: 'Place secured',
  // undrawn: under Place secured, when its balance is due (stage 3's review), with its amount from Stripe
  // when there is one (member review, 11 Oct): "£597+vat balance due 20 October"
  balanceDue: (date, amount = null) => (amount ? `${amount} balance due ${date}` : `Balance due ${date}`),
  // undrawn: the 1-2-1 client space, whose terms_line is empty
  clientSpaceTerms: 'Open for as long as you work with Adam.',
  // undrawn: a product's certificate under its row, as the certificate page promises (stage 4
  // review); the buttons are the certificate page's own (Certificate.dc.html)
  certificate: number => `Certificate ${number}`,
  certificateLabel: 'Your certificate',
  downloadPdf: 'Download the PDF',
  pdfDownloaded: 'PDF downloaded',
  copyCheck: 'Copy the check link',
  checkCopied: 'Check link copied',
  copyFailed: link => `The link couldn't be copied. Here it is to copy yourself: ${link}`,
  // undrawn: nothing that counts, for example an ended masterclass and nothing else
  noProgrammes: "You don't have a programme with us at the moment. Everything free is in your library.",

  // Your data
  yourData: 'Your data',
  askExport: 'Download what we hold about you',
  askDelete: 'Ask for your account to be deleted',
  // the board's "We will email you a download link within a day." changed (Adam, 5 Oct, question 7): the
  // email carries no link, it sends them here, where the copy waits under Your data
  exportAsked: "We'll email you within a day, when your copy is ready to download here.",
  deleteAsked: 'Request sent. Deleting your account removes your access to everything, so Adam will confirm with you by email first.',
  // undrawn
  dataFailed: "That didn't go through. Check your connection and try again.",
  // undrawn (stage 5): a copy Adam has made, downloadable for 7 days (data-export's email sends them here)
  exportReady: until => `Your copy of what we hold about you is ready. Download it by ${until}.`,
  downloadExport: 'Download your data',
  downloadFailed: "That didn't download. Check your connection and try again.",

  // Referrals (decision 55, 13 Oct; docs/referrals-spec.md 3.1): the products Adam has ticked for them,
  // each with their link, and the people they've introduced. No board draws it; the words are the
  // spec's, after the old site's Referrals page.
  referrals: {
    title: 'Referrals',
    nothing: 'Nothing to share just yet.',
    lede: "Know someone who'd get as much from it as you have? Share your link below. When they go ahead and meet the terms on that card, the fee comes to you.",
    // with lines but nothing ticked (Adam took the tick away), there's no link below to share (the review of 13 Oct)
    ledeNoLink: "The people you've introduced, and where each one is.",
    pending: 'Pending',
    owed: 'Owed to you',
    paid: 'Paid to you',
    yourLink: 'Your link',
    linkLabel: name => `Your link for ${name}`,
    copy: 'Copy',
    copied: 'Copied',
    copiedLine: 'Link copied.',
    copyFailed: link => `The link couldn't be copied. Here it is to copy yourself: ${link}`,
    whatsapp: 'WhatsApp',
    onItsWay: "Your link for this is on its way. Adam's setting it up.",
    details: "What you're referring",
    hide: 'Hide details',
    whoFor: "Who it's for",
    keyPoints: 'The key points',
    priceTerms: 'Price and terms',
    howTo: 'How to introduce it',
    neverSay: 'Never say',
    close: 'Your job is the introduction.',
    listTitle: 'Your referrals',
    empty: "No referrals yet. When someone uses your link, they'll show up here.",
    someone: 'Someone',
    statuses: { applied: 'Applied', joined: 'Joined', qualified: 'Qualified', paid: 'Paid', not_proceeding: 'Not proceeding' },
    loadFailed: "Your referrals didn't load. Check your connection and try again.",
    tryAgain: 'Try again',
  },

  // undrawn: the page's own rows didn't load
  loadFailedTitle: "Your account didn't load",
  loadFailedLine: `Check your connection and try again. If it keeps happening, email ${SITE.helpEmail}.`,
  tryAgain: 'Try again',
}

// The line for a function's refusal: its own message (lib/functions.js already gives the hub's own
// line when Supabase's gateway refused the token)
export function lineFor(e) {
  return e?.message || 'Something went wrong. Try again.'
}

// The password rule set-password applies (supabase/functions/set-password/logic.ts), checked here
// first so a member hears about it before anything is sent. The lines are the function's own.
export const PASSWORD = {
  minChars: 10,
  maxBytes: 72,
  tooShort: 'Your password needs at least 10 characters.',
  tooLong: 'Your password can be up to 72 characters.',
  sameAsEmail: "Choose a password that isn't your email address.",
}

export function checkPassword(password, email) {
  if ([...password.trim()].length < PASSWORD.minChars) return PASSWORD.tooShort
  if (new TextEncoder().encode(password).length > PASSWORD.maxBytes) return PASSWORD.tooLong
  if (email && password.trim().toLowerCase() === email.trim().toLowerCase()) return PASSWORD.sameAsEmail
  return null
}

// About you's label and a directory switch for each mastermind they hold, in their order (Profile.jsx;
// the product-types follow-ups, 13 Oct): { about, switches: [{ id, accelerator, label }] }. The
// Accelerator's switch is people.directory_opt_in and reads as always, word for word; another
// mastermind's is its row in hub.directory_listings
export const isAccelerator = p => p?.slug === 'accelerator'
export function directoryWords(products) {
  const about = products.length === 1 && isAccelerator(products[0]) ? COPY.about : COPY.aboutFor(products.length)
  const switches = products.map(p => ({
    id: p.id, accelerator: isAccelerator(p), label: isAccelerator(p) ? COPY.directory : COPY.directoryOf(p.name),
  }))
  return { about, switches }
}
